import {cn} from '@tempmee/core'
import {
  OutlineCheckIcon,
  OutlineExclamationCircleIcon,
  OutlineInformationCircleIcon,
  OutlineLightBulbIcon,
  OutlineSparklesIcon,
  OutlineWarning01Icon,
  OutlineXIcon,
} from '@tempmee/react-icons'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {AlertVariantsFactory} from './Variants'

export type AlertSeverity = 'info' | 'success' | 'warning' | 'error' | 'flagged' | 'cta'

const SEVERITY_ICON: Record<AlertSeverity, ReactNode> = {
  info: <OutlineInformationCircleIcon />,
  success: <OutlineCheckIcon />,
  warning: <OutlineWarning01Icon />,
  error: <OutlineExclamationCircleIcon />,
  flagged: <OutlineSparklesIcon />,
  cta: <OutlineLightBulbIcon />,
}

export interface AlertProps
  // `title` is our prop, so it's omitted from HTMLAttributes. The native inline
  // `style` attribute is preserved (the variant axis is named `variant`).
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Color + icon scheme. @default 'info' */
  severity?: AlertSeverity
  /** `standard` = tint, `filled` = solid fill with white text. @default 'standard' */
  variant?: 'filled' | 'standard'
  /** Bold title line. */
  title?: ReactNode
  /** Description text. */
  description?: ReactNode
  /** Override the default severity icon. */
  icon?: ReactNode
  /** Optional trailing action (e.g. a small outlined `<Button>`). */
  action?: ReactNode
  /** When provided, renders a close button that calls this handler. */
  onClose?: () => void
}

/**
 * Contextual feedback message for success, warning, error, or informational
 * system states. For brand-forward, non-semantic messaging (tips, promos,
 * announcements) use {@link Banner} instead.
 *
 * **Use an Alert when:**
 * - Communicating important state at the top of a page or inside a card.
 * - The message maps to a `severity`: info / success / warning / error / flagged / cta.
 *
 * **Do NOT use an Alert when:**
 * - The message is routine confirmation noise — reserve Alerts for things that matter.
 * - The content is promotional or brand emphasis — use {@link Banner}.
 *
 * **Guidelines:**
 * - Keep messages concise; pair the right `severity` icon to differentiate types.
 * - Provide `onClose` to make it dismissible when appropriate.
 * - Don't stack multiple Alerts.
 * - Never rely on color alone to convey meaning — the icon and copy must carry it.
 *
 * @see Banner — for non-semantic, brand-forward inline messaging.
 * @remarks Usage guidance synced from Figma via /sync-figma-guidelines — do not hand-edit.
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      severity = 'info',
      variant = 'standard',
      title,
      description,
      icon,
      action,
      onClose,
      ...props
    },
    ref,
  ) => {
    const hasBoth = title != null && description != null

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          AlertVariantsFactory({severity, variant}),
          hasBoth ? 'items-start' : 'items-center',
          className,
        )}
        {...props}
      >
        <span className="flex size-6 shrink-0 items-center justify-center [&_svg]:size-6">
          {icon ?? SEVERITY_ICON[severity]}
        </span>

        <div className="flex min-w-0 flex-1 flex-col justify-center leading-[1.6]">
          {title != null && <span className="text-[16px] font-bold">{title}</span>}
          {description != null && <span className="text-[16px] font-normal">{description}</span>}
        </div>

        {action != null && <div className="flex shrink-0 items-end self-stretch">{action}</div>}

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss"
            className="-m-0.5 flex size-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-black/5 [&_svg]:size-[22px]"
          >
            <OutlineXIcon />
          </button>
        )}
      </div>
    )
  },
)

Alert.displayName = 'Alert'
